import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import { Crown, ExternalLink, Share2, Twitter, Facebook, Linkedin, Copy, X } from "lucide-react";
import { toast } from "sonner";

/**
 * FounderNFTBadge
 *
 * Shows on a Founder Elder's public profile if they have a minted Founder
 * NFT on XRPL. Renders a gold-foil badge with serial #, links to the
 * XRPL Explorer (proof), and a Share button that opens a sheet with
 * pre-populated Twitter / Facebook / LinkedIn copy + the OG share card.
 *
 * The share image is served by the backend at
 * `/api/founder-nft/share-card/{user_id}.png` — a 1200×630 OG card that
 * social-media unfurls render natively. So every share = backlink +
 * branded image. Compound SEO + organic acquisition for free.
 *
 * Props:
 *   userId   — required, the elder's user ID
 *   isOwner  — true if the viewer IS the elder (shows extra "Share my NFT" CTA)
 *   compact  — small variant for inline use
 */
export default function FounderNFTBadge({ userId, isOwner = false, compact = false }) {
  const [nft, setNft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/xrpl/founder-nft/${userId}`);
        if (!cancelled) setNft(data || null);
      } catch {
        if (!cancelled) setNft(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading || !nft) return null;
  const serialPadded = String(nft.foundingNumber).padStart(4, "0");
  const explorerUrl = `https://livenet.xrpl.org/nft/${nft.nftokenId}`;

  if (compact) {
    return (
      <a
        href={explorerUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/40 text-amber-300 hover:from-amber-500/30 hover:to-yellow-500/30"
        data-testid={`founder-nft-compact-${nft.foundingNumber}`}
        title={`Founder #${serialPadded} — verified on XRPL`}
      >
        <Crown size={10} /> Founder #{serialPadded}
      </a>
    );
  }

  return (
    <>
      <div
        className="relative p-4 sm:p-5 rounded-md border border-amber-500/40 bg-gradient-to-br from-amber-950/30 via-yellow-900/20 to-amber-950/30"
        data-testid="founder-nft-badge"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-md bg-gradient-to-br from-amber-400 to-yellow-700 flex flex-col items-center justify-center text-amber-950 font-serif">
              <div className="text-[8px] uppercase tracking-[0.15em]">Founder</div>
              <div className="text-xl sm:text-2xl font-bold leading-none">#{serialPadded}</div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-mystic text-amber-300 flex items-center gap-1.5">
              <Crown size={11} /> On-Chain Founding Ambassador
            </div>
            <h3 className="font-serif text-xl mt-1">Permanently minted on the XRP Ledger</h3>
            <p className="text-sm text-foreground/70 mt-1.5">
              This is one of only <b>100</b> commemorative NFTs ever issued
              by Cauldron — proof of being among the founding 1% of practitioners.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <a
                href={explorerUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-amber-500/40 hover:bg-amber-500/10 text-xs uppercase tracking-wider text-amber-300"
                data-testid="founder-nft-explorer"
              >
                <ExternalLink size={11} /> View on XRPL
              </a>
              <button
                onClick={() => setShareOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500 text-amber-950 hover:bg-amber-400 text-xs uppercase tracking-wider font-medium"
                data-testid="founder-nft-share"
              >
                <Share2 size={11} /> {isOwner ? "Share My NFT" : "Share"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {shareOpen && (
        <ShareSheet
          userId={userId}
          serialPadded={serialPadded}
          foundingNumber={nft.foundingNumber}
          onClose={() => setShareOpen(false)}
          isOwner={isOwner}
        />
      )}
    </>
  );
}

function ShareSheet({ userId, serialPadded, foundingNumber, onClose, isOwner }) {
  const profileUrl = `${PUBLIC_BASE}/elders/${userId}`;
  const shareImg = `${PUBLIC_BASE}/api/founder-nft/share-card/${userId}.svg`;
  const tweetText = isOwner
    ? `I'm Founder #${serialPadded} of 500 on @CauldronApp — verified on-chain on the XRP Ledger. The marketplace for trusted spiritual practitioners. ✨`
    : `Verified Founding Ambassador #${serialPadded} on Cauldron — the marketplace for spiritual practitioners. On-chain proof on the XRP Ledger.`;

  const twitter = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(profileUrl)}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(profileUrl);
    toast.success("Link copied");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="founder-nft-share-sheet">
      <div className="bg-card border border-secondary/30 rounded-md max-w-lg w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 text-foreground/50 hover:text-foreground" data-testid="founder-nft-share-close">
          <X size={18} />
        </button>
        <div className="label-mystic">Share</div>
        <h2 className="font-serif text-2xl mt-1">Founder #{serialPadded} of 500</h2>

        <div className="mt-4 rounded overflow-hidden border border-amber-500/30">
          <img src={shareImg} alt={`Founder NFT #${foundingNumber}`} className="w-full" loading="lazy" />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <a href={twitter} target="_blank" rel="noreferrer"
             className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded bg-[#1d9bf0] text-white text-sm hover:opacity-90"
             data-testid="founder-nft-share-twitter">
            <Twitter size={14} /> Twitter / X
          </a>
          <a href={facebook} target="_blank" rel="noreferrer"
             className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded bg-[#1877f2] text-white text-sm hover:opacity-90"
             data-testid="founder-nft-share-facebook">
            <Facebook size={14} /> Facebook
          </a>
          <a href={linkedin} target="_blank" rel="noreferrer"
             className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded bg-[#0a66c2] text-white text-sm hover:opacity-90"
             data-testid="founder-nft-share-linkedin">
            <Linkedin size={14} /> LinkedIn
          </a>
          <button onClick={copyLink}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded border border-secondary/30 text-sm hover:bg-secondary/10"
                  data-testid="founder-nft-share-copy">
            <Copy size={14} /> Copy Link
          </button>
        </div>

        <div className="text-xs text-foreground/50 mt-4 text-center">
          Every share helps grow the Cauldron community — and keeps spiritual practitioners safe.
        </div>
      </div>
    </div>
  );
}
